/**
 * BrokerFinancingFXModal.tsx
 * Modal for configuring broker-level overnight financing and currency conversion defaults.
 *
 * Uses a staged-local edit model: edits are held locally inside the modal until the
 * user explicitly confirms ("Done"). On cancel/dismiss, no changes are transferred.
 * The parent (BrokersTab) is responsible for persisting via BrokersContext save flow.
 *
 * Reuses OvernightFinancingTabContent and CurrencyConversionTabContent in edit mode
 * to maintain UI consistency with the per-node fee editor.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Settings2 } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Tabs,
  Tab,
} from '@enfomentis/ui';

import type { OvernightFinancingStructure } from '@/types/shared/feeStructures/OvernightFinancingStructure';
import type { CurrencyConversionStructure } from '@/types/shared/feeStructures/CurrencyConversionStructure';

import { OvernightFinancingTabContent } from './feeEditor/OvernightFinancingTabContent';
import { CurrencyConversionTabContent } from './feeEditor/CurrencyConversionTabContent';

// =============================================================================
// Props
// =============================================================================

interface BrokerFinancingFXModalProps {
  isOpen: boolean;
  onClose: () => void;
  overnightDefault: OvernightFinancingStructure | undefined;
  conversionDefault: CurrencyConversionStructure | undefined;
  accountCurrency: string | null;
  onConfirm: (
    overnight: OvernightFinancingStructure | undefined,
    conversion: CurrencyConversionStructure | undefined,
  ) => void;
}

// =============================================================================
// Default structures for new configurations
// =============================================================================

const DEFAULT_OVERNIGHT: OvernightFinancingStructure = {
  benchmark: 'sofr',
  adminSpread: 0,
  adminSpreadPrecision: 2,
  frequency: 'daily',
  assetClass: 'forex', // Required by type; not rendered; generic default
  hasTripleCharges: false,
  verificationStatus: 'userOverride',
};

const DEFAULT_CONVERSION: CurrencyConversionStructure = {
  rateType: 'spot',
  triggers: ['trading'],
  usesRealTimeRates: true,
  verificationStatus: 'userOverride',
};

// =============================================================================
// Tab Keys
// =============================================================================

type FinancingFXTabKey = 'overnight' | 'conversion';

// =============================================================================
// Component
// =============================================================================

export const BrokerFinancingFXModal: React.FC<BrokerFinancingFXModalProps> = ({
  isOpen,
  onClose,
  overnightDefault,
  conversionDefault,
  accountCurrency,
  onConfirm,
}) => {
  // ---------------------------------------------------------------------------
  // Staged-local state (held until Done/Cancel)
  // ---------------------------------------------------------------------------

  const [stagedOvernight, setStagedOvernight] = useState<OvernightFinancingStructure | undefined>(undefined);
  const [stagedConversion, setStagedConversion] = useState<CurrencyConversionStructure | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<FinancingFXTabKey>('overnight');

  // ---------------------------------------------------------------------------
  // Open-edge initialisation (false → true transition only)
  // ---------------------------------------------------------------------------

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      // Open edge: deep-clone props into staged state and reset tab
      setStagedOvernight(
        overnightDefault ? structuredClone(overnightDefault) : undefined,
      );
      setStagedConversion(
        conversionDefault ? structuredClone(conversionDefault) : undefined,
      );
      setActiveTab('overnight');
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, overnightDefault, conversionDefault]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleOvernightChange = useCallback((overnight: OvernightFinancingStructure): void => {
    setStagedOvernight(overnight);
  }, []);

  const handleOvernightRemove = useCallback((): void => {
    setStagedOvernight(undefined);
  }, []);

  const handleConversionChange = useCallback((conversion: CurrencyConversionStructure): void => {
    setStagedConversion(conversion);
  }, []);

  const handleConversionRemove = useCallback((): void => {
    setStagedConversion(undefined);
  }, []);

  const handleConfigureOvernight = useCallback((): void => {
    setStagedOvernight({ ...DEFAULT_OVERNIGHT });
  }, []);

  const handleConfigureConversion = useCallback((): void => {
    setStagedConversion({ ...DEFAULT_CONVERSION, triggers: [...DEFAULT_CONVERSION.triggers] });
  }, []);

  const handleDone = useCallback((): void => {
    onConfirm(stagedOvernight, stagedConversion);
  }, [onConfirm, stagedOvernight, stagedConversion]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      placement="top-center"
      scrollBehavior="inside"
      isDismissable
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <Settings2 size={18} className="text-text-secondary" />
          Financing &amp; FX Settings
        </ModalHeader>

        <ModalBody className="pb-2">
          <Tabs
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as FinancingFXTabKey)}
            variant="underlined"
            color="primary"
            fullWidth
            aria-label="Financing & FX settings tabs"
            classNames={{ tabList: 'gap-4' }}
          >
            {/* ── Overnight Financing Tab ── */}
            <Tab key="overnight" title="Overnight Financing">
              <div className="pt-2">
                {stagedOvernight ? (
                  <OvernightFinancingTabContent
                    overnight={stagedOvernight}
                    mode="edit"
                    accountCurrency={accountCurrency}
                    onChange={handleOvernightChange}
                    onRemove={handleOvernightRemove}
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-default-300 p-4 text-center">
                    <p className="text-sm text-text-tertiary mb-3">
                      No overnight financing defaults configured for this broker.
                    </p>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleConfigureOvernight}
                    >
                      Configure Overnight Financing
                    </Button>
                  </div>
                )}
              </div>
            </Tab>

            {/* ── Currency Conversion Tab ── */}
            <Tab key="conversion" title="Currency Conversion">
              <div className="pt-2">
                {stagedConversion ? (
                  <CurrencyConversionTabContent
                    conversion={stagedConversion}
                    mode="edit"
                    accountCurrency={accountCurrency}
                    onChange={handleConversionChange}
                    onRemove={handleConversionRemove}
                    hideApplicableCurrencyPairs
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-default-300 p-4 text-center">
                    <p className="text-sm text-text-tertiary mb-3">
                      No currency conversion defaults configured for this broker.
                    </p>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleConfigureConversion}
                    >
                      Configure Currency Conversion
                    </Button>
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose}>
            Cancel
          </Button>
          <Button color="primary" onPress={handleDone}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
